/* Byg dit sæt — behaviour for sections/bs-builder-main.liquid.
   Data: the section prints every product of every room (plus its finish siblings) as JSON. Here we group them into
   designs (one design = one piece, available in one or more finishes), let the customer pick room, finish and pieces,
   and add the whole set to the cart in one /cart/add.js call, so Impact's cart drawer opens like on the rest of the site. */
(() => {
  if (window.BastantBuilder) return;

  const FINISH_ORDER = ['borstet', 'poleret', 'bruneret', 'staal', 'sort', 'hvid', 'kobber'];
  const NUMBER_WORDS = ['nul', 'en', 'to', 'tre', 'fire', 'fem', 'seks', 'syv', 'otte', 'ni', 'ti', 'elleve', 'tolv'];
  const ICON_PLUS = '<svg class="bs-icon bs-icon--plus" width="17" height="17" viewBox="0 0 17 17" fill="none" aria-hidden="true" focusable="false"><path d="M8.5 3.54V13.46M3.54 8.5H13.46" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const ICON_CHECK = '<svg class="bs-icon bs-icon--check" width="14" height="14" viewBox="0 0 17 17" fill="none" aria-hidden="true" focusable="false"><path d="M3.54 8.85L6.73 12.04L13.46 5.31" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /* Same output as snippets/bastant-lp-money.liquid: 1.199 kr. / 999 kr. / 49,50 kr. */
  const formatKr = (cents) => {
    const value = Math.round(Number(cents) || 0);
    const kr = Math.floor(value / 100);
    const ore = value % 100;
    const whole = String(kr).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${whole}${ore ? ',' + String(ore).padStart(2, '0') : ''} kr.`;
  };

  const fill = (template, tokens) =>
    String(template || '').replace(/\[([a-z_]+)\]/g, (match, key) => (key in tokens ? tokens[key] : match));

  const esc = (value) =>
    String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const norm = (value) => String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();

  const joinNames = (names, and) => {
    if (names.length <= 1) return names.join('');
    return `${names.slice(0, -1).join(', ')} ${and} ${names[names.length - 1]}`;
  };

  const routes = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* Copied from assets/bastant-lp.js: ask Impact for its bundled sections, post to /cart/add.js, then fire
     variant:add + cart:change so the drawer opens and the header count updates. */
  async function addToCart(items, source) {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );

    const response = await fetch(`${routes()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sections.join(',') }),
    });
    const json = await response.json();

    if (!response.ok) {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      const error = new Error(json.description || json.message || '');
      error.fromShop = Boolean(json.description || json.message);
      throw error;
    }

    const settings = (window.themeVariables && window.themeVariables.settings) || {};
    if (settings.cartType === 'page') {
      window.location.href = `${routes()}cart`;
      return;
    }

    const cart = await (await fetch(`${routes()}cart.js`)).json();
    cart.sections = json.sections;

    (source || document.documentElement).dispatchEvent(
      new CustomEvent('variant:add', {
        bubbles: true,
        detail: { items: json.items || [json], cart },
      })
    );
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } })
    );
  }

  /* Variant tuple: [id, title, price, compare, available, qty (-1 = not tracked)] */
  const isAvailable = (product) => Boolean(product && product.v.some((v) => v[4]));

  /* Group a room's products into designs. Products are linked by the design name of their link_variants
     collection (or the title before the comma). If one design then has two products in the same finish
     (e.g. "Sæbeholder til 1 flaske" and "til 2 flasker" share one link collection), it is split by title. */
  function buildDesigns(room, products) {
    const groups = new Map();
    room.handles.forEach((handle, index) => {
      const p = products[handle];
      if (!p || !p.f || !FINISH_ORDER.includes(p.f) || !p.v || !p.v.length) return;
      const name = (p.g || p.b || p.t).trim();
      const key = norm(name);
      if (!groups.has(key)) groups.set(key, { name, order: index, members: [] });
      groups.get(key).members.push(p);
    });

    const designs = [];
    groups.forEach((group) => {
      let parts = [{ name: group.name, members: group.members, split: false }];
      const finishes = group.members.map((p) => p.f);
      if (new Set(finishes).size !== finishes.length) {
        const byBase = new Map();
        group.members.forEach((p) => {
          const k = norm(p.b);
          if (!byBase.has(k)) byBase.set(k, { name: p.b, members: [], split: true });
          byBase.get(k).members.push(p);
        });
        if (byBase.size > 1) parts = [...byBase.values()];
      }

      parts.forEach((part, partIndex) => {
        const byFinish = {};
        part.members.forEach((p) => {
          const current = byFinish[p.f];
          if (!current || (!isAvailable(current) && isAvailable(p))) byFinish[p.f] = p;
        });
        const named = part.members.find((p) => p.n);
        const bases = new Set(part.members.map((p) => norm(p.b)));
        /* One shared title ("Køkkenrulleholder") reads better than the link collection's name ("Køkkenrulleholdere"). */
        const name = named ? named.n : bases.size === 1 ? part.members[0].b : part.name;
        designs.push({ key: norm(name), name, byFinish, order: group.order + partIndex / 100 });
      });
    });

    designs.sort((a, b) => a.order - b.order);
    return designs;
  }

  class Builder {
    constructor(root) {
      this.root = root;
      const data = JSON.parse(root.querySelector('[data-bs-builder-data]').textContent);
      this.t = JSON.parse(root.querySelector('[data-bs-builder-texts]').textContent);
      this.products = data.products || {};
      this.rooms = (data.rooms || []).map((room) => ({
        ...room,
        designs: buildDesigns(room, this.products),
        selected: null,
        overrides: {},
      }));
      this.variantChoice = {};

      this.el = {
        rooms: root.querySelector('[data-rooms]'),
        finishes: root.querySelector('[data-finishes]'),
        list: root.querySelector('[data-list]'),
        partsLabel: root.querySelector('[data-parts-label]'),
        stepRoom: root.querySelector('[data-step-room]'),
        stepFinish: root.querySelector('[data-step-finish]'),
        stepParts: root.querySelector('[data-step-parts]'),
        photo: root.querySelector('[data-summary-photo]'),
        img: root.querySelector('[data-summary-img]'),
        title: root.querySelector('[data-summary-title]'),
        swatch: root.querySelector('[data-summary-swatch]'),
        meta: root.querySelector('[data-summary-meta]'),
        lines: root.querySelector('[data-summary-lines]'),
        total: root.querySelector('[data-summary-total]'),
        compare: root.querySelector('[data-summary-compare]'),
        add: root.querySelector('[data-add]'),
        addLabel: root.querySelector('[data-add-label]'),
        error: root.querySelector('[data-error]'),
        uspShipping: root.querySelector('[data-usp-shipping]'),
        uspGuarantee: root.querySelector('[data-usp-guarantee]'),
        soldout: root.querySelector('[data-soldout]'),
        soldoutText: root.querySelector('[data-soldout-text]'),
        notify: root.querySelector('[data-notify]'),
        notifyError: root.querySelector('[data-notify-error]'),
        notifyDone: root.querySelector('[data-notify-done]'),
      };

      if (!this.rooms.length) {
        root.querySelector('.bs-builder__body').hidden = true;
        return;
      }

      const params = new URLSearchParams(window.location.search);
      const wantedRoom = params.get('rum');
      this.room = this.rooms.find((r) => r.key === wantedRoom) || this.rooms[0];
      const wantedFinish = params.get('finish');
      this.finish = this.pickFinish(this.room, wantedFinish || this.t.defaultFinish);

      this.renderFinishButtons();
      this.bind();
      this.setStickyOffset();
      this.render();
    }

    label(finish) {
      return (this.t.finishes[finish] || [finish, finish])[0];
    }

    short(finish) {
      return (this.t.finishes[finish] || [finish, finish])[1];
    }

    finishCount(room, finish) {
      return room.designs.filter((d) => d.byFinish[finish]).length;
    }

    pickFinish(room, wanted) {
      if (wanted && this.finishCount(room, wanted)) return wanted;
      if (this.finish && this.finishCount(room, this.finish)) return this.finish;
      return FINISH_ORDER.find((f) => this.finishCount(room, f)) || wanted || 'borstet';
    }

    /* The product a design shows in the current finish (or in the finish the customer switched this piece to). */
    productFor(room, design) {
      const finish = room.overrides[design.key] || this.finish;
      return { product: design.byFinish[finish] || null, finish, override: Boolean(room.overrides[design.key]) };
    }

    variantFor(product) {
      const chosen = this.variantChoice[product.h];
      const variants = product.v;
      return variants.find((v) => v[0] === chosen) || variants.find((v) => v[4]) || variants[0];
    }

    visibleDesigns(room) {
      return room.designs.filter((d) => this.productFor(room, d).product);
    }

    ensureSelection(room) {
      if (room.selected) return;
      room.selected = new Set();
      let left = Number(room.preselect) || 0;
      this.visibleDesigns(room).forEach((design) => {
        if (left <= 0) return;
        const { product } = this.productFor(room, design);
        if (isAvailable(product)) {
          room.selected.add(design.key);
          left -= 1;
        }
      });
    }

    /* Pieces that will go in the cart: chosen, shown in this finish and in stock. */
    effectiveItems(room) {
      return this.visibleDesigns(room)
        .map((design) => {
          const info = this.productFor(room, design);
          const variant = this.variantFor(info.product);
          return { design, ...info, variant };
        })
        .filter((item) => room.selected.has(item.design.key) && item.variant && item.variant[4]);
    }

    renderFinishButtons() {
      this.el.finishes.innerHTML = FINISH_ORDER.map(
        (f) => `<button type="button" class="bs-builder__finish" role="radio" aria-checked="false" data-finish="${f}" hidden>
          <span class="bs-builder__ring"><span class="bs-swatch bs-swatch--${f}"></span></span>
          <span class="bs-builder__finish-name"><span class="bs-builder__finish-long">${esc(this.label(f))}</span><span class="bs-builder__finish-short">${esc(this.short(f))}</span></span>
          <span class="bs-builder__finish-count" data-finish-count></span>
        </button>`
      ).join('');
    }

    bind() {
      this.el.rooms.addEventListener('click', (event) => {
        const button = event.target.closest('[data-room]');
        if (button) this.selectRoom(button.dataset.room);
      });
      this.el.finishes.addEventListener('click', (event) => {
        const button = event.target.closest('[data-finish]');
        if (button) this.selectFinish(button.dataset.finish);
      });
      [this.el.rooms, this.el.finishes].forEach((group) => group.addEventListener('keydown', (event) => this.arrowKeys(event, group)));

      this.el.list.addEventListener('click', (event) => {
        const row = event.target.closest('[data-design]');
        if (!row) return;
        const key = row.dataset.design;
        const alt = event.target.closest('[data-alt]');
        if (alt) {
          this.room.overrides[key] = alt.dataset.alt;
          this.room.selected.add(key);
          this.render({ focus: { key, part: 'toggle' } });
          return;
        }
        if (event.target.closest('[data-reset]')) {
          delete this.room.overrides[key];
          this.render({ focus: { key, part: 'toggle' } });
          return;
        }
        if (event.target.closest('select, a, input, label')) return;
        if (row.classList.contains('is-out')) return;
        if (this.room.selected.has(key)) this.room.selected.delete(key);
        else this.room.selected.add(key);
        this.render({ focus: event.target.closest('[data-toggle]') ? { key, part: 'toggle' } : null });
      });
      this.el.list.addEventListener('change', (event) => {
        const select = event.target.closest('[data-variant]');
        if (!select) return;
        this.variantChoice[select.dataset.variant] = Number(select.value);
        this.render({ focus: { key: select.closest('[data-design]').dataset.design, part: 'variant' } });
      });

      this.el.add.addEventListener('click', () => this.addSet());
      if (this.el.notify) this.el.notify.addEventListener('submit', (event) => this.subscribe(event));
    }

    arrowKeys(event, group) {
      const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
      if (!(event.key in keys)) return;
      const buttons = [...group.querySelectorAll('[role="radio"]:not([hidden])')];
      const index = buttons.indexOf(document.activeElement);
      if (index < 0) return;
      event.preventDefault();
      const next = buttons[(index + keys[event.key] + buttons.length) % buttons.length];
      next.click();
      next.focus();
    }

    selectRoom(key) {
      const room = this.rooms.find((r) => r.key === key);
      if (!room || room === this.room) return;
      this.room = room;
      this.finish = this.pickFinish(room, this.finish);
      this.render();
    }

    selectFinish(finish) {
      if (finish === this.finish) return;
      this.finish = finish;
      this.room.overrides = {};
      this.render();
    }

    setStickyOffset() {
      const header = document.querySelector('.bs-header');
      if (!header) return;
      const sticky = [header, header.parentElement, header.closest('.shopify-section')].find(
        (el) => el && getComputedStyle(el).position === 'sticky'
      );
      if (sticky) this.root.style.setProperty('--bs-sticky-top', `${sticky.offsetHeight}px`);
    }

    syncUrl() {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('rum', this.room.key);
        url.searchParams.set('finish', this.finish);
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (e) {
        /* ignore */
      }
    }

    render(options = {}) {
      const room = this.room;
      const t = this.t;
      this.ensureSelection(room);

      /* Rooms */
      this.el.rooms.querySelectorAll('[data-room]').forEach((button) => {
        const r = this.rooms.find((x) => x.key === button.dataset.room);
        button.setAttribute('aria-checked', String(r === room));
        button.tabIndex = r === room ? 0 : -1;
        const count = button.querySelector('[data-room-count]');
        if (count && r) count.textContent = r.designs.length === 1 && t.roomCountOne ? t.roomCountOne : fill(t.roomCount, { count: r.designs.length });
      });

      /* Finishes */
      this.el.finishes.querySelectorAll('[data-finish]').forEach((button) => {
        const f = button.dataset.finish;
        const n = this.finishCount(room, f);
        button.hidden = n === 0;
        button.setAttribute('aria-checked', String(f === this.finish));
        button.tabIndex = f === this.finish ? 0 : -1;
        button.querySelector('[data-finish-count]').textContent =
          n === room.designs.length && n > 1
            ? fill(t.finishAll, { count: n })
            : n === 1 && t.finishCountOne ? t.finishCountOne : fill(t.finishCount, { count: n });
      });

      /* Pieces */
      const finishLabel = this.label(this.finish);
      const visible = this.visibleDesigns(room);
      const items = this.effectiveItems(room);
      this.el.partsLabel.textContent = fill(t.partsLabel, { finish: finishLabel });

      if (!visible.length) {
        this.el.list.innerHTML = `<li class="bs-builder__empty">${esc(fill(t.empty, { finish: finishLabel.toLowerCase() }))}</li>`;
      } else {
        this.el.list.innerHTML = visible.map((design) => this.rowHtml(room, design)).join('');
      }

      /* Steps */
      this.el.stepRoom.textContent = room.title;
      this.el.stepFinish.textContent = finishLabel;
      this.el.stepParts.textContent = fill(t.stepParts, { selected: items.length, total: visible.length });

      this.renderSummary(room, items, visible);
      this.syncUrl();

      if (options.focus) {
        const row = this.el.list.querySelector(`[data-design="${CSS.escape(options.focus.key)}"]`);
        const target = row && row.querySelector(options.focus.part === 'variant' ? '[data-variant]' : '[data-toggle]');
        if (target) target.focus({ preventScroll: true });
      }
    }

    rowHtml(room, design) {
      const t = this.t;
      const { product, finish, override } = this.productFor(room, design);
      const variant = this.variantFor(product);
      const available = isAvailable(product);
      const selected = available && room.selected.has(design.key);
      const finishLabel = this.label(finish).toLowerCase();

      let stockClass = 'in';
      let stockText = t.stockIn;
      if (!available) {
        stockClass = 'out';
        stockText = fill(t.stockOut, { finish: finishLabel });
      } else if (variant[5] > 0 && variant[5] <= 3) {
        stockClass = 'low';
        stockText = fill(t.stockLow, { qty: variant[5] });
      } else if (product.pr) {
        stockClass = 'production';
        stockText = t.stockProduction;
      }

      const price = formatKr(variant[2]);
      const compare = variant[3] > variant[2] ? `<s>${formatKr(variant[3])}</s>` : '';

      let extra = '';
      if (product.v.length > 1) {
        const options = product.v
          .map((v) => `<option value="${v[0]}"${v[0] === variant[0] ? ' selected' : ''}${v[4] ? '' : ' disabled'}>${esc(v[1])}</option>`)
          .join('');
        extra += `<label class="bs-sr" for="bsb-v-${product.h}">${esc(product.o || product.t)}</label><select class="bs-builder__variant" id="bsb-v-${esc(product.h)}" data-variant="${esc(product.h)}">${options}</select>`;
      }
      if (!available) {
        const alts = FINISH_ORDER.filter((f) => f !== finish && isAvailable(design.byFinish[f]));
        if (alts.length) {
          extra += `<span>${esc(t.altLabel)}</span>${alts
            .map((f) => `<button type="button" class="bs-builder__alt" data-alt="${f}"><span class="bs-swatch bs-swatch--${f}"></span>${esc(this.label(f))}</button>`)
            .join('')}`;
        }
      }
      if (override) {
        extra += `<span>${esc(fill(t.overrideNote, { finish: finishLabel }))}</span>`;
        if (design.byFinish[this.finish]) {
          extra += `<button type="button" class="bs-builder__reset" data-reset>${esc(fill(t.overrideReset, { finish: this.label(this.finish).toLowerCase() }))}</button>`;
        }
      }

      const img = product.i
        ? `<img src="${esc(product.i)}" alt="" width="76" height="76" loading="lazy">`
        : '';
      const toggleLabel = fill(selected ? t.deselect : t.select, { name: design.name });

      return `<li class="bs-builder__row${selected ? ' is-selected' : ''}${available ? '' : ' is-out'}" data-design="${esc(design.key)}">
        <div class="bs-builder__thumb">${img}</div>
        <div class="bs-builder__info">
          <p class="bs-builder__name">${esc(design.name)}</p>
          ${product.d ? `<p class="bs-builder__desc">${esc(product.d)}</p>` : ''}
          <p class="bs-builder__mprice">${compare}${price}</p>
          <p class="bs-stock bs-builder__stock bs-builder__stock--${stockClass}"><span class="bs-stock__dot" aria-hidden="true"></span>${esc(stockText)}</p>
          ${extra ? `<div class="bs-builder__extra">${extra}</div>` : ''}
        </div>
        <p class="bs-builder__price">${compare}${price}</p>
        <button type="button" class="bs-builder__toggle" data-toggle aria-pressed="${selected}" aria-label="${esc(toggleLabel)}"${available ? '' : ' disabled'}>${ICON_PLUS}${ICON_CHECK}</button>
      </li>`;
    }

    renderSummary(room, items, visible) {
      const t = this.t;
      const finishLabel = this.label(this.finish);
      const count = items.length;
      const total = items.reduce((sum, item) => sum + item.variant[2], 0);
      const compareTotal = items.reduce((sum, item) => sum + Math.max(item.variant[3], item.variant[2]), 0);

      const lead = items[0];
      const image = (lead && lead.product.il) || room.image;
      this.el.photo.hidden = !image;
      if (image && this.el.img.getAttribute('src') !== image) {
        this.el.img.src = image;
        this.el.img.alt = lead ? lead.product.t : room.summary || room.title;
      }

      this.el.title.textContent = room.summary || room.title;
      this.el.swatch.className = `bs-swatch bs-builder__summary-swatch bs-swatch--${this.finish}`;
      this.el.meta.textContent = count === 1
        ? fill(t.summaryMetaOne, { finish: finishLabel, count })
        : fill(t.summaryMeta, { finish: finishLabel, count });

      this.el.lines.innerHTML = items
        .map((item) => {
          const note = item.override ? ` <small>· ${esc(this.label(item.finish).toLowerCase())}</small>` : '';
          const variantNote = item.product.v.length > 1 ? ` <small>· ${esc(item.variant[1].toLowerCase())}</small>` : '';
          return `<li class="bs-builder__line"><span class="bs-builder__line-name">${esc(item.design.name)}${note}${variantNote}</span><span class="bs-builder__line-price">${formatKr(item.variant[2])}</span></li>`;
        })
        .join('');

      this.el.total.textContent = formatKr(total);
      if (compareTotal > total) {
        this.el.compare.hidden = false;
        this.el.compare.textContent = formatKr(compareTotal);
      } else {
        this.el.compare.hidden = true;
      }

      if (!this.busy) {
        this.el.add.disabled = count === 0;
        this.el.addLabel.textContent = count === 0 ? t.buttonEmpty : t.button;
      }

      /* USPs */
      const threshold = Number(t.shippingThreshold) || 0;
      const shippingText = total >= threshold
        ? fill(t.uspShippingFree, { threshold: formatKr(threshold) })
        : fill(t.uspShippingMissing, { threshold: formatKr(threshold), missing: formatKr(threshold - total) });
      this.setUsp(this.el.uspShipping, threshold > 0 ? shippingText : '');
      if (this.el.uspGuarantee) {
        const guarantee = count === 1
          ? t.uspGuaranteeOne
          : fill(t.uspGuarantee, { count, count_word: NUMBER_WORDS[count] || String(count) });
        this.setUsp(this.el.uspGuarantee, count ? guarantee : '');
      }

      /* Sold-out pieces in this finish */
      const soldOut = visible
        .map((design) => ({ design, ...this.productFor(room, design) }))
        .filter((x) => !isAvailable(x.product));
      this.soldOutVariants = soldOut.map((x) => x.product.v[0][0]);
      if (soldOut.length && t.soldoutNote) {
        this.el.soldout.hidden = false;
        this.el.soldoutText.textContent = fill(t.soldoutNote, {
          names: joinNames(soldOut.map((x) => x.design.name), t.and),
          finish: finishLabel.toLowerCase(),
        });
        const key = soldOut.map((x) => x.product.h).join(',');
        if (this.notifyKey !== key) {
          this.notifyKey = key;
          if (this.el.notify) this.el.notify.hidden = false;
          if (this.el.notifyDone) this.el.notifyDone.hidden = true;
          if (this.el.notifyError) this.el.notifyError.hidden = true;
        }
      } else {
        this.el.soldout.hidden = true;
      }
    }

    setUsp(li, text) {
      if (!li) return;
      li.hidden = !text;
      li.querySelector('[data-usp-text]').textContent = text;
      li.classList.toggle('ends-dot', /\.\s*$/.test(text));
    }

    showError(message) {
      this.el.error.textContent = message;
      this.el.error.hidden = !message;
    }

    async addSet() {
      const items = this.effectiveItems(this.room);
      if (!items.length || this.busy) return;
      this.showError('');
      this.busy = true;
      this.el.add.setAttribute('aria-busy', 'true');
      this.el.add.disabled = true;

      try {
        await addToCart(items.map((item) => ({ id: item.variant[0], quantity: 1 })), this.el.add);
        this.el.addLabel.innerHTML = `${ICON_CHECK.replace('bs-icon--check', 'bs-icon--added')} ${esc(this.t.buttonAdded)}`;
        window.clearTimeout(this.addedTimer);
        this.addedTimer = window.setTimeout(() => this.render(), 2500);
      } catch (error) {
        this.showError(error.fromShop ? error.message : this.t.error);
      } finally {
        this.busy = false;
        this.el.add.setAttribute('aria-busy', 'false');
        this.el.add.disabled = false;
      }
    }

    async subscribe(event) {
      event.preventDefault();
      const form = this.el.notify;
      const button = form.querySelector('[type="submit"]');
      const email = form.querySelector('[name="email"]').value.trim();
      const company = form.dataset.company;
      const showErr = (message) => {
        this.el.notifyError.textContent = message;
        this.el.notifyError.hidden = !message;
      };

      showErr('');
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        showErr(this.t.notifyInvalid);
        return;
      }

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      try {
        const results = await Promise.all(
          (this.soldOutVariants || []).map((variant) =>
            fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(company)}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/vnd.api+json', revision: '2024-10-15' },
              body: JSON.stringify({
                data: {
                  type: 'back-in-stock-subscription',
                  attributes: {
                    profile: { data: { type: 'profile', attributes: { email } } },
                    channels: ['EMAIL'],
                  },
                  relationships: {
                    variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variant}` } },
                  },
                },
              }),
            })
          )
        );
        if (!results.length || results.some((r) => !r.ok)) throw new Error('notify');
        form.hidden = true;
        this.el.notifyDone.hidden = false;
      } catch (error) {
        showErr(this.t.notifyError);
      } finally {
        button.disabled = false;
        button.setAttribute('aria-busy', 'false');
      }
    }
  }

  const instances = new WeakMap();
  function init(scope) {
    (scope || document).querySelectorAll('[data-bs-builder]').forEach((root) => {
      if (instances.has(root)) return;
      try {
        instances.set(root, new Builder(root));
      } catch (error) {
        console.error('Byg dit sæt:', error);
      }
    });
  }

  window.BastantBuilder = { init, formatKr, buildDesigns };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  /* Theme editor */
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest('[data-bs-builder]');
    const builder = root && instances.get(root);
    if (builder && event.target.dataset.room) builder.selectRoom(event.target.dataset.room);
  });
})();
